import React, { useState, useEffect } from 'react';
import { ImageAppletModalPreview } from '../components';
import { convertHeicFileToJpeg, convertTiffFileToJpeg } from 'helpers/heicConverter';
import { UploadFile } from 'components/applets/app-upload';
import { getFileObject } from 'helpers/FileHelpers';
import localization from 'localization';

type ImageAppletModalPreviewWithConversionProps = {
  src: string | undefined;
  alt?: string;
  fileType?: string;
};

const ImageAppletModalPreviewWithConversion = ({
  src,
  alt,
  fileType,
}: ImageAppletModalPreviewWithConversionProps) => {
  const [convertedSrc, setConvertedSrc] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!src) {
      setConvertedSrc(null);
      setIsConverting(false);
      setImageError(false);
      return;
    }

    // Reset state when src changes
    // Don't attempt conversion here - let the image try to load first
    // Only convert if it fails to load (via handleImageError)
    setConvertedSrc(null);
    setIsConverting(false);
    setImageError(false);
  }, [src, fileType]);

  const handleImageError = async () => {
    // Only attempt conversion if image actually failed to load
    // and we don't already have a converted file
    if (!src || isConverting || convertedSrc) {
      setImageError(true);
      return;
    }

    const fileObject = getFileObject({ fileUrl: src, fileType });

    if (!fileObject || fileObject.type !== 'image/*') {
      setImageError(true);
      return;
    }

    const fileAsUploadFile: UploadFile = {
      name: fileObject.name,
      type: fileObject.type,
      uri: src,
      mime: fileType,
    };

    // Always attempt conversion with force: true when image fails to load
    // This handles cases where TIFF/HEIC files come from S3 without extension
    // and with generic MIME type (image/*). The conversion functions will validate
    // internally if the file is actually a valid HEIC/TIFF when force is true.
    setIsConverting(true);
    setImageError(false);

    // Try converting as TIFF first (more common than HEIC)
    // with force to attempt even if not detected
    try {
      const tiffConverted = await convertTiffFileToJpeg(fileAsUploadFile, true);
      if (tiffConverted) {
        setConvertedSrc(tiffConverted.uri);
        setIsConverting(false);
        setImageError(false);
        return;
      }
    } catch (error) {
      // Silently continue to try HEIC
    }

    // Try converting as HEIC (with force to attempt even if not detected)
    try {
      const heicConverted = await convertHeicFileToJpeg(fileAsUploadFile, true);
      if (heicConverted) {
        setConvertedSrc(heicConverted.uri);
        setIsConverting(false);
        setImageError(false);
        return;
      }
    } catch (error) {
      // Conversion failed
    }

    // If both conversions fail, mark as error
    setIsConverting(false);
    setImageError(true);
  };

  // Show loading state while converting
  if (isConverting) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>{localization.convertingHeicImage}</p>
      </div>
    );
  }

  // Show error state if image fails to load and conversion also failed
  if (imageError && !convertedSrc) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>{localization.imageCannotBeDisplayed}</p>
      </div>
    );
  }

  // If no src, don't render anything
  if (!src) {
    return null;
  }

  // Use converted source if available, otherwise use original
  const imageSrc = convertedSrc || src;

  return (
    <ImageAppletModalPreview
      src={imageSrc}
      alt={alt}
      onError={convertedSrc ? undefined : handleImageError}
    />
  );
};

export default ImageAppletModalPreviewWithConversion;

